import { useCallback, useMemo } from 'react';
import _pick from 'lodash/pick';
import _omit from 'lodash/omit';
import { ProductSubcategory } from 'api/types/product';
import { IGuestProduct } from 'api/guest_sdk';

interface Props {
  products: Array<IGuestProduct>;
}

export const ALL_PRODUCTS_KEY = 'All products';

const DEFAULT_CATEGORIES = Object.entries(ProductSubcategory)
  .slice(0, -1)
  .reduce(
    (res, [_, val]) => {
      return { ...res, [val]: 0 };
    },
    { [ALL_PRODUCTS_KEY]: 0 },
  );

export default function useCategories({ products }: Props) {
  const categories = useMemo(
    () =>
      products?.reduce((res, product) => {
        const { subcategory } = product;
        if (!subcategory) {
          return res;
        }
        const lastValue = res[subcategory];
        if (!lastValue) {
          return { ...res, [subcategory]: 1 };
        }

        return { ...res, [subcategory]: lastValue + 1 };
      }, DEFAULT_CATEGORIES),
    [products?.length],
  );

  const getOptions = useCallback((categories) => {
    return Object.keys(categories).map((value) => ({
      value,
      label: value === ALL_PRODUCTS_KEY ? value : `${value} (${categories[value]})`,
    }));
  }, []);

  const baseCategories = _pick(categories, Object.keys(DEFAULT_CATEGORIES));
  const otherCategories = _omit(categories, Object.keys(baseCategories));

  return { categories: baseCategories, otherCategories, getOptions };
}
